import { html, i18n, TemplateElement, toString } from '@webtides/element-js';
import '../../../../legacy-library/webcomponents/svg-icon/svg-icon';
import '../../../../legacy-library/webcomponents/accordion-element/accordion-element';
import '../opening-hours/opening-hours';
import style from './retailer-card.css';
import { classMap } from 'lit-html/directives/class-map.js';
import recursiveFindKeyValue from '../../../../js/util/recursiveFindKeyValue';

export default class RetailerCard extends TemplateElement {
    constructor(options) {
        super({ shadowRender: false, styles: [style], ...options });
    }

    connected() {
        this.requestUpdate();
    }

    afterUpdate() {
        this.$refs.accordeon.open = false;
    }

    properties() {
        return {
            retailer: {},
        };
    }

    injectProperties() {
        return {
            retailerCardDisplayOptions: {},
        };
    }

    get formattedAddress() {
        return `${this.retailer?.street},${this.retailer?.zipCode},${this.retailer.city}`;
    }

    get isQualityWorkshop() {
        return this.retailer?.competencies?.SERVICES?.qswerkstatt ?? false;
    }

    get showQualityWorkshop() {
        return this.retailerCardDisplayOptions?.showQualityWorkshopLogo && this.isQualityWorkshop;
    }

    get hasCompetencyLogo() {
        return this.retailerCardDisplayOptions?.cmsCompetencyLogo;
    }

    get hasCompetencyLogoOne() {
        return this.retailerCardDisplayOptions?.cmsCompetencyLogoOne;
    }

    get hasLogoInHeader() {
        return (
            (this.hasCompetency && this.hasCompetencyLogo) ||
            (this.hasCompetency1 && this.hasCompetencyLogoOne) ||
            this.showQualityWorkshop
        );
    }

    get hasCompetency() {
        return this.isCompetency(this.retailer?.competencies, this.retailerCardDisplayOptions?.competencyValue);
    }

    get hasCompetency1() {
        return this.isCompetency(this.retailer?.competencies, this.retailerCardDisplayOptions?.competencyValue1);
    }

    get retailerName() {
        return this.retailer?.name;
    }

    whitespaceToDash(string) {
        return string.trim().replace(/\s/g, '-');
    }

    /**
     * Determines if the provided object contains the specified key recursively.
     * @param {Object} obj - The object to search within.
     * @param {string} key - The key to search for within the object.
     * @returns {boolean} - True if the key is found within the object recursively, otherwise false.
     */
    isCompetency(obj, key) {
        return recursiveFindKeyValue(obj, key);
    }

    templateLinks() {
        return Object.entries(this.retailer?.links).map(([key, link]) => {
            const i18nKey = `retailerSearch.links.${key}`;
            const i18nText = i18n(i18nKey);
            if (i18nKey === i18nText) {
                // means there is no i18nKey to map the link label to links should not be rendered
                return null;
            }

            return html`
                <a
                    href="${link}"
                    target="_blank"
                    class="text-link block mb-2 ml-0 h-auto normal-case"
                    data-id="${this.whitespaceToDash(this.retailerName)}-${key}"
                >
                    ${i18nText}
                </a>
            `;
        });
    }

    defaultIcon() {
        return html` <svg-icon name="map-pin" class="map-pin-icon h-4 w-4 sm:h-6 sm:w-6 md:mx-auto "></svg-icon>`;
    }

    qualityWorkshopLogo() {
        const IMAGE_URL = `${window.siteConfig().publicPath}/assets/images/ZEG_Qualitaetswerkstatt_Online-Logo_2.svg`;
        return html`
            <div
                class="row-start-1 col-start-3 col-span-full self-center md:row-start-auto md:col-start-auto md:col-span-4"
            >
                <img class="object-contain max-w-full" src="${IMAGE_URL}" alt="ZEG_Qualitaetswerkstatt_Online-Logo_2" />
            </div>
        `;
    }

    competencyLogoTemplate(imageUrl = '') {
        return html`
            <div
                class="row-start-1 col-start-3 col-span-full self-center md:row-start-auto md:col-start-auto md:col-span-4"
            >
                <img class="object-contain max-w-full max-h-[64px] mx-auto" src="${imageUrl}" alt="competency-logo" />
            </div>
        `;
    }

    templateWebsiteLink() {
        if (!this.retailerCardDisplayOptions?.showRetailerLink || !this.retailer?.url) return;

        const url = `${this.retailer?.url.includes('//') ? '' : '//'}${this.retailer?.url}`;
        return html`<a
            href="${url}"
            target="_blank"
            class="text-link block mb-2 ml-0 h-auto normal-case"
            data-id="${this.whitespaceToDash(this.retailerName)}-web"
        >
            ${i18n('cta.toRetailerWebsite.label')}
        </a>`;
    }

    templateRetailerFeatureLogo() {
        if (!this.hasLogoInHeader) {
            return '';
        }

        if (this.hasCompetency && this.hasCompetencyLogo) {
            return this.competencyLogoTemplate(this.retailerCardDisplayOptions?.cmsCompetencyLogo);
        } else if (this.hasCompetency1 && this.hasCompetencyLogoOne) {
            return this.competencyLogoTemplate(this.retailerCardDisplayOptions?.cmsCompetencyLogoOne);
        } else if (this.showQualityWorkshop) {
            return this.qualityWorkshopLogo();
        } else {
            return '';
        }
    }

    template() {
        let classes = { 'md:col-span-9': !this.hasLogoInHeader };
        return html`
            <accordion-element cloak="true" ref="accordeon" class="${this.hasCompetency ? 'is-competency' : ''}">
                <div slot="header" class="w-full p-4 relative">
                    <div class="grid grid-cols-6 gap-4 md:grid-cols-12 xl:gap-8">
                        <div
                            class="${[
                                !this.hasLogoInHeader ? 'col-span-5 md:col-span-2' : '',
                                this.hasLogoInHeader ? 'col-span-2' : '',
                            ].join(
                                ' '
                            )} pin-label-container flex flex-wrap gap-2 items-center md:flex-col md:self-center"
                        >
                            ${this.defaultIcon()}
                            <div class="label inline-block whitespace-nowrap font-thin md:text-center">
                                ${this.retailer?.distance.toLocaleString('de-DE', { maximumFractionDigits: 1 })} km
                            </div>
                        </div>
                        <div class="col-span-5 ${classMap(classes)}">
                            <div class="text-md">
                                <p class="retailer-name font-normal text-black uppercase">${this.retailer?.name}</p>
                                <p class="font-thin text-grey-600">
                                    ${this.retailer?.street}, ${this.retailer?.zipCode} ${this.retailer?.city}
                                </p>
                            </div>
                        </div>
                        ${this.templateRetailerFeatureLogo()}
                        <div
                            class="row-start-2 col-start-6 flex items-start justify-center md:row-start-auto md:col-start-auto md:items-center"
                        >
                            <svg-icon
                                name="accordion_open"
                                class="accordion-icon open-icon h-4 w-4 sm:h-6 sm:w-6 mx-auto"
                            ></svg-icon>
                            <svg-icon
                                name="accordion_close"
                                class="accordion-icon close-icon h-4 w-4 sm:h-6 sm:w-6 mx-auto"
                            ></svg-icon>
                        </div>
                    </div>
                </div>
                <div slot="content" class="w-full p-4" cloak="display-none">
                    <div class="flex flex-wrap">
                        <div class="w-full md:w-8/12 mx-auto pb-8">
                            <opening-hours
                                days="${toString(this.retailer?.openingHours?.days)}"
                                class="block mb-8"
                            ></opening-hours>
                            ${this.retailer?.phone &&
                            html`<a
                                href="tel:${this.retailer?.phone}"
                                class="text-link block mb-2 h-auto normal-case"
                                data-id="${this.whitespaceToDash(this.retailerName)}-phone"
                            >
                                ${this.retailer?.phone}
                            </a>`}
                            ${this.email &&
                            html`<a
                                href="mailto:${this.retailer?.email}"
                                class="text-link block mb-2 ml-0 h-auto normal-case"
                                data-id="${this.whitespaceToDash(retailerName)}-mail"
                            >
                                ${i18n('cta.writeEmail.label')}
                            </a>`}
                            ${this.templateLinks()} ${this.templateWebsiteLink()}

                            <a
                                href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                                    this.formattedAddress
                                )}"
                                target="_blank"
                                class="text-link block h-auto ml-0 normal-case"
                                data-id="${this.whitespaceToDash(this.retailerName)}-map"
                            >
                                ${i18n('cta.showAtGoogleMaps.label')}
                            </a>
                        </div>
                    </div>
                </div>
            </accordion-element>
        `;
    }
}
